import { useEffect, useState } from 'react'
import { Box, Grid, styled } from '@mui/material'

import { useAppDispatch, useAppSelector } from 'app/state'
import { RequestStatus } from 'features/common'
import Loader from 'features/common/ui/utils/Loader'
import useInfiniteScrollPagination from 'features/common/ui/Pagination/useInfiniteScrollPagination'

import { fetchMapsRequest, setEditMapError } from 'features/maps/state/mapsSlice'
import useAccountHasMaps from 'features/maps/state/useAccountHasMaps'
import { MAPS_REMOTE_PAGE_SIZE } from 'features/maps/api/maps'

import MapsEmpty from './MapsEmpty'
import MapsCounter from './MapsCounter'
import MapsSample from '../MapsSample/MapsSample'
import EmptyState from 'features/common/ui/utils/EmptyState'
import NoMapIcon from 'assets/icons/carto-icon-maps-no-map.svg?react'
import { FormattedMessage } from 'react-intl'
import { createMetricRequest } from 'features/metrics/state/metricsSlice'
import { MetricEventType } from 'features/metrics/api/metrics'
import InfiniteScrollableMapsList from './InfiniteScrollableMapsList'
import { useCartoDW } from 'features/connections/hooks/connectionsHooks'
import NoConnectionsOrCdwPlaceholder from 'features/connections/ui/NoConnectionsOrCdwPlaceholder'
import useNotifications from 'features/notifications/ui/useNotifications'
import useUserRoles from 'features/account/state/useUserRoles'
import { useMapFilters } from 'features/maps/utils/useMapFilters'

const RootGrid = styled(Grid, {
  shouldForwardProp: (prop) => prop !== 'withApps'
})<{
  withApps?: boolean
}>(({ withApps, theme }) => ({
  position: 'relative',
  minHeight: withApps ? theme.spacing(43) : undefined,

  '& .infinite-scroll-component__outerdiv': {
    width: '100%'
  }
}))

const GeneralLoader = styled(Loader)(() => ({
  position: 'absolute',
  width: '100%',
  left: 0,
  top: '50%',
  transform: 'translateY(-50%)'
}))

const NotFound = styled(Box)(() => ({
  width: '100%',
  display: 'flex'
}))

function MapsGallery() {
  const dispatch = useAppDispatch()
  const mapsFilterApplied = useAppSelector((state) => state.maps.myMapsFilters)
  const mapsMetadata = useAppSelector((state) => state.maps.myMapsMetadata)
  const mapsStatus = useAppSelector((state) => state.maps.myMapsStatus)
  const mapEditError = useAppSelector((state) => state.maps.mapEditError)

  const maps = useAppSelector((state) => state.maps.myMaps)
  const { hasActiveFilters } = useMapFilters()
  const { showConnectionsBanner } = useCartoDW()
  const [initialLoading, setInitialLoading] = useState(true)
  const { sendErrorNotification, sendErrorNotificationLocalize } = useNotifications()
  const { userHasEditionRole } = useUserRoles()

  const { accountHasMaps, loading: accountDataLoading } = useAccountHasMaps()
  const hasMaps = accountHasMaps && !accountDataLoading

  const { localPage } = useInfiniteScrollPagination({
    remoteList: maps,
    metadata: mapsMetadata,
    requestStatus: mapsStatus,
    remotePageSize: MAPS_REMOTE_PAGE_SIZE
  })

  useEffect(() => {
    if (mapsStatus === RequestStatus.Idle) {
      dispatch(fetchMapsRequest(mapsFilterApplied))
    } else if (mapsStatus === RequestStatus.Fulfilled) {
      dispatch(createMetricRequest(MetricEventType.MapsListed))
    }

    if (mapEditError) {
      if (mapEditError.error === 'This map is locked by someone else') {
        sendErrorNotificationLocalize('maps.errorCurrentMapLocked')
      } else {
        sendErrorNotification(mapEditError.error)
      }
      dispatch(setEditMapError(undefined))
    }
  }, [dispatch, mapsStatus, mapsFilterApplied, mapEditError, sendErrorNotification, sendErrorNotificationLocalize])

  const thereAreMaps = maps.length > 0

  useEffect(() => {
    if (initialLoading && mapsStatus !== RequestStatus.Pending) {
      setInitialLoading(false)
    }
  }, [initialLoading, mapsStatus])

  const displayMapCounter = !!mapsMetadata?.total && !mapsFilterApplied.search && !!localPage.length
  const displayScrollableMapList = thereAreMaps && mapsMetadata

  if (showConnectionsBanner && userHasEditionRole) {
    return <NoConnectionsOrCdwPlaceholder />
  }
  return (
    <RootGrid container spacing={3} withApps={thereAreMaps || initialLoading}>
      {initialLoading ? (
        <GeneralLoader />
      ) : (
        <>
          {displayMapCounter && <MapsCounter mapsCount={mapsMetadata?.total} />}

          {displayScrollableMapList ? (
            <InfiniteScrollableMapsList />
          ) : hasMaps && (maps.length > 0 || hasActiveFilters) ? (
            <>
              <NotFound mb={1.5} mt={2}>
                <EmptyState tight={true} icon={NoMapIcon} caption={<FormattedMessage id="maps.filters.noResults" />} />
              </NotFound>
            </>
          ) : (
            <>
              <MapsEmpty />
              {userHasEditionRole && <MapsSample />}
            </>
          )}
        </>
      )}
    </RootGrid>
  )
}

export default MapsGallery
